import { cn } from "@vellum/ui";
import type { HubFolderItem } from "@vellum/vellum-api";

import {
  CARD_CLASSES,
  CardBody,
  CardIcon,
  EditableCard,
  FOLDER_ICON_TILE_CLASSES,
  INTERACTIVE_CARD_CLASSES,
  type EditableCardProps,
} from "@/components/LinkCard";

function LinkCount({ count }: { count: number }) {
  return (
    <span className="mt-2 text-xs font-semibold text-gray-500">
      {count === 1 ? "1 link" : `${count} links`}
    </span>
  );
}

/** A folder in the grid; opening it shows its links in a dialog. */
export function FolderCard({
  folder,
  onOpen,
}: {
  folder: HubFolderItem;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(CARD_CLASSES, INTERACTIVE_CARD_CLASSES)}
    >
      <div className="flex w-full items-start justify-between">
        <CardIcon
          icon={folder.icon}
          fallback="folder"
          tileClassName={FOLDER_ICON_TILE_CLASSES}
        />
        <span
          aria-hidden="true"
          className="iconoir-nav-arrow-right text-lg text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-green-700"
        />
      </div>
      <CardBody title={folder.title} description={folder.description} />
      <LinkCount count={folder.links.length} />
    </button>
  );
}

type EditableFolderCardProps = Omit<
  EditableCardProps,
  "title" | "description" | "visibleToRoles"
> & { folder: HubFolderItem; onOpen: () => void };

/** Manage-mode folder card: the shared edit frame plus an "Open" action. */
export function EditableFolderCard({
  folder,
  onOpen,
  ...props
}: EditableFolderCardProps) {
  return (
    <EditableCard
      {...props}
      title={folder.title}
      description={folder.description}
      visibleToRoles={folder.visible_to_roles ?? []}
      icon={
        <CardIcon
          icon={folder.icon}
          fallback="folder"
          tileClassName={FOLDER_ICON_TILE_CLASSES}
        />
      }
    >
      <div className="mt-2 flex items-center justify-between">
        <LinkCount count={folder.links.length} />
        <button
          type="button"
          onClick={onOpen}
          className="text-xs font-semibold text-blue-500 hover:underline"
        >
          Open folder
        </button>
      </div>
    </EditableCard>
  );
}
